import React, { useContext, useEffect, useState } from 'react'
import { Table } from 'react-bootstrap'
import { Badge, Button, Center, Loader, Text } from '@mantine/core'
import { toast } from 'react-toastify'
import { useAuth } from '../../modules/auth'
import {
  getAttendanceEditRequests,
  approveAttendanceEditRequest,
  rejectAttendanceEditRequest,
} from '../../../api/AttendanceAPI'
//import { request } from 'http'
import { AttendanceRecord } from '../../types/AttendanceTypes'
import roleContext from '../../context/roleContext'


interface AttendanceEditRequest {
  id: number
  employee_code: string
  employee_name?: string
  attendance_date: string
  old_in_time?: string
  old_out_time?: string
  requested_in_time?: string
  requested_out_time?: string
  reason: string
  status: 'PENDING' | 'APPROVED' | 'REJECTED'
  requested_by: string
}

const AttendanceEditApproval: React.FC = () => {
  const { currentUser } = useAuth()
  const [loading, setLoading] = useState(false)
  const [requests, setRequests] = useState<AttendanceEditRequest[]>([])
  const [allRequests, setAllRequests] = useState<AttendanceEditRequest[]>([])
  const [searchText, setSearchText] = useState('')


  // Only HR / RM / Admin / SuperAdmin should access
  //const roleName = currentUser?.role?.name?.toLowerCase() || '';
  const roleState = useContext(roleContext)    
  const roleName = roleState.roleName?.toLowerCase().trim() || '';
  const isSystemAdmin = currentUser?.email?.toLowerCase() === 'admin@ctrine.com' ||
    currentUser?.email?.toLowerCase() === 'admin@ctrine.in';
  const isAuthorized =
    currentUser?.isRmOrHr ||
    currentUser?.isAdmin ||
    isSystemAdmin ||
    roleName.includes('admin') ||
    roleName === 'super admin' ||
    roleName === 'super_admin' ||
    roleName === 'system administrator';

  // Debug logging
  console.log('AttendanceEditApproval - Authorization Check:', {
    isRmOrHr: currentUser?.isRmOrHr,
    isAdmin: currentUser?.isAdmin,
    roleName: currentUser?.role?.name,
    isAuthorized
  });

  const loadRequests = async () => {
    setLoading(true)
    try {
      const res = await getAttendanceEditRequests()
      const sortedData = (res?.data || []).sort(
        (a: AttendanceEditRequest, b: AttendanceEditRequest) =>
          new Date(b.attendance_date).getTime() -
          new Date(a.attendance_date).getTime()
      )
      setRequests(sortedData)
      setAllRequests(sortedData) // for filtering
    } catch (err) {
      toast.error('Failed to load edit requests')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let filtered = [...allRequests]
    if (searchText.trim() !== '') {
      const search = searchText.toLowerCase()
      filtered = filtered.filter((req) => {
        const empName = (req.employee_name || '').toLowerCase()
        const empCode = (req.employee_code || '').toLowerCase()
        const status = (req.status || '').toLowerCase()
        return (
          empName.includes(search) ||
          empCode.includes(search) ||
          status.includes(search)
        )
      })
    }
    setRequests(filtered)
  }, [searchText, allRequests])

  useEffect(() => {
    if (isAuthorized) {
      loadRequests()
    }
  }, [isAuthorized])

  const handleApprove = async (req: AttendanceEditRequest) => {
    try {
      await approveAttendanceEditRequest(req.id)

      // UPDATE UI IMMEDIATELY
      setRequests(prev =>
        prev.map(r =>
          r.id === req.id
            ? { ...r, status: 'APPROVED' }
            : r
        )
      )

      toast.success('Attendance edit approved & attendance updated')

      // optional safety reload (recommended)
      setTimeout(loadRequests, 500)
    } catch (err) {
      toast.error('Approval failed')
    }
  }


  const handleReject = async (req: AttendanceEditRequest) => {
    try {
      await rejectAttendanceEditRequest(req.id, 'Rejected by HR')

      setRequests(prev =>
        prev.map(r =>
          r.id === req.id
            ? {
              ...r,
              status: 'REJECTED',
              reason: `${r.reason} | Rejected by HR`,
            }
            : r
        )
      )

      toast.success('Attendance edit rejected')

      setTimeout(loadRequests, 500)
    } catch (err) {
      toast.error('Rejection failed')
    }
  }

  //Date Wises Filter Added 
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  // const handleFilter = () => {
  //   if (!startDate || !endDate) {
  //     toast.error('Please select both dates')
  //     return
  //   }

  //   const filtered = requests.filter((req) => {
  //     const reqDate = new Date(req.attendance_date).toISOString().split('T')[0]
  //     return reqDate >= startDate && reqDate <= endDate
  //   })

  //   setRequests(filtered)
  // }
  const handleFilter = () => {
    if (!startDate || !endDate) {
      toast.error('Please select both dates')
      return
    }

    const filtered = allRequests.filter((req) => {
      const reqDate = new Date(req.attendance_date)
        .toISOString()
        .split('T')[0]
      return reqDate >= startDate && reqDate <= endDate
    })
    setRequests(filtered)
  }


  if (!isAuthorized) {
    return (
      <Center py={60}>
        <Text color="red">You are not authorized to view this page</Text>
      </Center>
    )
  }
  const ReasonText = ({ text }) => {
    const [expanded, setExpanded] = useState(false)

    if (!text) return <span>—</span>

    const words = text.split(" ")
    const limit = 5

    const isLong = words.length > limit

    const shortText = words.slice(0, limit).join(" ")

    return (
      <div style={{ maxWidth: "250px", wordWrap: "break-word" }}>
        {expanded ? text : shortText}

        {isLong && (
          <span
            style={{ color: "#0d6efd", cursor: "pointer", marginLeft: "5px" }}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? " Read Less" : "... Read More"}
          </span>
        )}
      </div>
    )
  }

  return (
    <div style={{ backgroundColor: '#F4F8FD', padding: '16px' }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '14px',
        border: '1px solid #E2ECF7',
        boxShadow: 'rgba(18, 59, 115, 0.06) 0px 4px 12px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px',
          borderBottom: '1px solid #E2ECF7',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          {/* LEFT SIDE - TITLE WITH SVG ICON */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '2px' }}>
              <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="calendar-check" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" style={{ fontSize: '24px', color: '#3B82F6', width: '24px', height: '24px' }}>
                <path fill="currentColor" d="M128 0c17.7 0 32 14.3 32 32V64H288V32c0-17.7 14.3-32 32-32s32 14.3 32 32V64h48c26.5 0 48 21.5 48 48v48H0V112C0 85.5 21.5 64 48 64H96V32c0-17.7 14.3-32 32-32zM0 192H448V464c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V192zM329 305c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-95 95-47-47c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l64 64c9.4 9.4 24.6 9.4 33.9 0L329 305z"></path>
              </svg>
              <h4 style={{
                color: '#123B73',
                fontWeight: '600',
                fontSize: '18px',
                marginBottom: '0'
              }}>Attendance Edit Approvals</h4>
            </div>
            <p style={{
              color: '#64748B',
              fontSize: '12px',
              marginBottom: '0'
            }}>
              Review and approve or reject employee attendance edit requests
            </p>
          </div>

          {/* RIGHT SIDE - REFRESH BUTTON */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={loadRequests}
              title="Refresh Data"
              style={{
                backgroundColor: '#FFFFFF',
                color: '#64748B',
                border: '1px solid #E2ECF7',
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="arrows-rotate" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" style={{ fontSize: '12px', width: '12px', height: '12px' }}>
                <path fill="currentColor" d="M105.1 202.6c7.7-21.8 20.2-42.3 37.8-59.8c62.5-62.5 163.8-62.5 226.3 0L386.3 160H336c-17.7 0-32 14.3-32 32s14.3 32 32 32H463.5c0 0 0 0 0 0h.4c17.7 0 32-14.3 32-32V64c0-17.7-14.3-32-32-32s-32 14.3-32 32v51.2L414.4 97.6c-87.5-87.5-229.3-87.5-316.8 0C73.2 122 55.6 150.7 44.8 181.4c-5.9 16.7 2.9 34.9 19.5 40.8s34.9-2.9 40.8-19.5zM39 289.3c-5 1.5-9.8 4.2-13.7 8.2c-4 4-6.7 8.8-8.1 14c-.3 1.2-.6 2.5-.8 3.8c-.3 1.7-.4 3.4-.4 5.1V448c0 17.7 14.3 32 32 32s32-14.3 32-32V396.9l17.6 17.5 0 0c87.5 87.4 229.3 87.4 316.7 0c24.4-24.4 42.1-53.1 52.9-83.7c5.9-16.7-2.9-34.9-19.5-40.8s-34.9 2.9-40.8 19.5c-7.7 21.8-20.2 42.3-37.8 59.8c-62.5 62.5-163.8 62.5-226.3 0l-.1-.1L125.6 352H176c17.7 0 32-14.3 32-32s-14.3-32-32-32H48.4c-1.6 0-3.2 .1-4.8 .3s-3.1 .5-4.6 1z"></path>
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {/* SEARCH AND FILTER ROW */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '12px',
          padding: '0 24px 16px 24px',
          flexWrap: 'wrap'
        }}>
          <div style={{ marginBottom: '0' }}>
            <label style={{
              color: '#123B73',
              fontSize: '12px',
              fontWeight: '600',
              marginBottom: '4px',
              display: 'block'
            }}>Search</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Search..."
                style={{
                  width: '260px',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E2ECF7',
                  borderRadius: '4px',
                  padding: '12px 16px',
                  fontSize: '12px',
                  color: '#64748B'
                }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          <div style={{ marginBottom: '0' }}>
            <label style={{
              color: '#123B73',
              fontSize: '12px',
              fontWeight: '600',
              marginBottom: '4px',
              display: 'block'
            }}>Start Date</label>
            <div style={{ position: 'relative' }}>
              <input
                type="date"
                className="form-control"
                style={{
                  width: '150px',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E2ECF7',
                  borderRadius: '4px',
                  padding: '12px 16px',
                  fontSize: '12px',
                  color: '#64748B'
                }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
          </div>

          <div style={{ marginBottom: '0' }}>
            <label style={{
              color: '#123B73',
              fontSize: '12px',
              fontWeight: '600',
              marginBottom: '4px',
              display: 'block'
            }}>End Date</label>
            <div style={{ position: 'relative' }}>
              <input
                type="date"
                className="form-control"
                style={{
                  width: '150px',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E2ECF7',
                  borderRadius: '4px',
                  padding: '12px 16px',
                  fontSize: '12px',
                  color: '#64748B'
                }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-end' }}>
            <button
              onClick={handleFilter}
              style={{
                backgroundColor: '#3B82F6',
                color: '#FFFFFF',
                border: 'none',
                padding: '12px 16px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="magnifying-glass" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" style={{ fontSize: '12px', width: '12px', height: '12px' }}>
                <path fill="currentColor" d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"></path>
              </svg>
              Filter
            </button>

            <button
              onClick={() => {
                setRequests(allRequests)
                setStartDate('')
                setEndDate('')
              }}
              style={{
                backgroundColor: '#FFFFFF',
                color: '#64748B',
                border: '1px solid #E2ECF7',
                padding: '12px 16px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="eraser" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" style={{ fontSize: '12px', width: '12px', height: '12px' }}>
                <path fill="currentColor" d="M290.7 57.4L57.4 290.7c-25 25-25 65.5 0 90.5l80 80c12 12 28.3 18.7 45.3 18.7H288h9.4H512c17.7 0 32-14.3 32-32s-14.3-32-32-32H387.9L518.6 285.3c25-25 25-65.5 0-90.5L381.3 57.4c-25-25-65.5-25-90.5 0zM297.4 416H288l-105.4 0-80-80L227.3 211.3 364.7 348.7 297.4 416z"></path>
              </svg>
              Clear
            </button>
          </div>
        </div>

        <div style={{ padding: '20px' }}>
        {loading ? (
          <Center py={40}>
            <Loader />
          </Center>
        ) : requests.length === 0 ? (
          <Center py={40}>
            <Text color="dimmed">No pending edit requests</Text>
          </Center>
        ) : (
          <div className="table-responsive">
            <table style={{
              backgroundColor: '#FFFFFF',
              borderCollapse: 'collapse',
              width: '100%',
              fontSize: '12px'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#F4F8FD' }}>
                  <th style={{
                    color: '#123B73',
                    fontWeight: '600',
                    fontSize: '12px',
                    padding: '12px 16px',
                    borderBottom: '1px solid #E2ECF7',
                    textAlign: 'left',
                    whiteSpace: 'nowrap'
                  }}>Employee Code</th>
                  <th style={{
                    color: '#123B73',
                    fontWeight: '600',
                    fontSize: '12px',
                    padding: '12px 16px',
                    borderBottom: '1px solid #E2ECF7',
                    textAlign: 'left',
                    whiteSpace: 'nowrap'
                  }}>Employee Name</th>
                  <th style={{
                    color: '#123B73',
                    fontWeight: '600',
                    fontSize: '12px',
                    padding: '12px 16px',
                    borderBottom: '1px solid #E2ECF7',
                    textAlign: 'left',
                    whiteSpace: 'nowrap'
                  }}>Date</th>
                  <th style={{
                    color: '#123B73',
                    fontWeight: '600',
                    fontSize: '12px',
                    padding: '12px 16px',
                    borderBottom: '1px solid #E2ECF7',
                    textAlign: 'left',
                    whiteSpace: 'nowrap'
                  }}>Old In</th>
                  <th style={{
                    color: '#123B73',
                    fontWeight: '600',
                    fontSize: '12px',
                    padding: '12px 16px',
                    borderBottom: '1px solid #E2ECF7',
                    textAlign: 'left',
                    whiteSpace: 'nowrap'
                  }}>Old Out</th>
                  <th style={{
                    color: '#123B73',
                    fontWeight: '600',
                    fontSize: '12px',
                    padding: '12px 16px',
                    borderBottom: '1px solid #E2ECF7',
                    textAlign: 'left',
                    whiteSpace: 'nowrap'
                  }}>Requested In</th>
                  <th style={{
                    color: '#123B73',
                    fontWeight: '600',
                    fontSize: '12px',
                    padding: '12px 16px',
                    borderBottom: '1px solid #E2ECF7',
                    textAlign: 'left',
                    whiteSpace: 'nowrap'
                  }}>Requested Out</th>
                  <th style={{
                    color: '#123B73',
                    fontWeight: '600',
                    fontSize: '12px',
                    padding: '12px 16px',
                    borderBottom: '1px solid #E2ECF7',
                    textAlign: 'left',
                    whiteSpace: 'nowrap'
                  }}>Reason</th>
                  <th style={{
                    color: '#123B73',
                    fontWeight: '600',
                    fontSize: '12px',
                    padding: '12px 16px',
                    borderBottom: '1px solid #E2ECF7',
                    textAlign: 'left',
                    whiteSpace: 'nowrap'
                  }}>Status</th>
                  <th style={{
                    color: '#123B73',
                    fontWeight: '600',
                    fontSize: '12px',
                    padding: '12px 16px',
                    borderBottom: '1px solid #E2ECF7',
                    textAlign: 'left',
                    whiteSpace: 'nowrap'
                  }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(req => (
                  <tr 
                    key={req.id} 
                    style={{ 
                      borderBottom: '1px solid #E2ECF7',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F4F8FD'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{
                      padding: '8px 12px',
                      color: '#123B73',
                      fontWeight: '600',
                      fontSize: '12px',
                      verticalAlign: 'middle'
                    }}>{req.employee_code}</td>
                    <td style={{
                      padding: '8px 12px',
                      color: '#123B73',
                      fontWeight: '400',
                      fontSize: '12px',
                      verticalAlign: 'middle'
                    }}>{req.employee_name || '—'}</td>
                    <td style={{
                      padding: '8px 12px',
                      color: '#64748B',
                      fontWeight: '400',
                      fontSize: '12px',
                      verticalAlign: 'middle'
                    }}>
                      {req.attendance_date
                        ? new Date(req.attendance_date).toLocaleDateString('en-IN')
                        : '—'}
                    </td>
                    <td style={{
                      padding: '8px 12px',
                      color: '#64748B',
                      fontWeight: '400',
                      fontSize: '12px',
                      verticalAlign: 'middle'
                    }}>{req.old_in_time || '—'}</td>
                    <td style={{
                      padding: '8px 12px',
                      color: '#64748B',
                      fontWeight: '400',
                      fontSize: '12px',
                      verticalAlign: 'middle'
                    }}>{req.old_out_time || '—'}</td>
                    <td style={{
                      padding: '8px 12px',
                      color: '#64748B',
                      fontWeight: '400',
                      fontSize: '12px',
                      verticalAlign: 'middle'
                    }}>{req.requested_in_time || '—'}</td>
                    <td style={{
                      padding: '8px 12px',
                      color: '#64748B',
                      fontWeight: '400',
                      fontSize: '12px',
                      verticalAlign: 'middle'
                    }}>{req.requested_out_time || '—'}</td>
                    <td style={{
                      padding: '8px 12px',
                      verticalAlign: 'middle',
                      maxWidth: '300px'
                    }}>
                      <ReasonText text={req.reason} />
                    </td>
                    <td style={{
                      padding: '8px 12px',
                      verticalAlign: 'middle'
                    }}>
                      {req.status === 'PENDING' ? (
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          backgroundColor: '#FFF5D9',
                          color: '#D97706',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          <span style={{
                            width: '6px',
                            height: '6px',
                            backgroundColor: '#D97706',
                            borderRadius: '50%'
                          }}></span>
                          {req.status}
                        </div>
                      ) : (
                        <Badge
                          color={req.status === 'APPROVED' ? 'green' : 'red'}
                          style={{
                            fontSize: '10px',
                            fontWeight: '600',
                            padding: '3px 8px',
                            borderRadius: '12px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          {req.status}
                        </Badge>
                      )}
                    </td>
                    <td style={{
                      padding: '8px 12px',
                      verticalAlign: 'middle'
                    }}>
                      {req.status === 'PENDING' ? (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleApprove(req)}
                            style={{
                              backgroundColor: '#E8F8F0',
                              color: '#16A34A',
                              border: '1px solid #16A34A20',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(req)}
                            style={{
                              backgroundColor: '#FDECEC',
                              color: '#EF4444',
                              border: '1px solid #EF444420',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <Badge
                          color={req.status === 'APPROVED' ? 'green' : 'red'}
                          style={{
                            fontSize: '11px',
                            fontWeight: '600',
                            padding: '6px 14px',
                            borderRadius: '20px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          {req.status}
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
    </div>
  )
}

export default AttendanceEditApproval
